import { Alert, Box, Button, Snackbar, Stack } from '@mui/material'
import {
  emptyRecord,
  evaluateCondition,
  preparePayload,
  updateAtIndex,
} from '../utilities/fieldUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useClearDraft,
  useDraft,
  useEntityMeta,
  useSetDraft,
  useSetMeta,
} from '../context/MetaContext'
import { useNavigate, useParams } from 'react-router-dom'

import DetailCard from '../components/cards/DetailCard'
import DetailPageTemplate from './DetailPageTemplate'
import { HistoryRounded } from '@mui/icons-material'
import HistoryTimeLine from '../components/HistoryTimeline'
import Masonry from '@mui/lab/Masonry'
import SidePanel from '../components/SidePanel'
import { debounce } from 'lodash'
import fetchWithAuth from '../utilities/fetchWithAuth'
import { formatDateTime } from '../utilities/formatDateTime'
import { resolveNavPath } from '../utilities/pathMapping'
import { usePageTitle } from '../context/PageTitleContext'
import { useScrollToNew } from '../hooks/useScrollToNew'
import { useUser } from '../context/CurrentUserContext'

const EntityDetailTemplate = ({ endpoint, responsibleRoles }) => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const entityMeta = useEntityMeta(endpoint)
  const entityMetaRef = useRef(entityMeta)
  const setMeta = useSetMeta()
  const draft = useDraft(endpoint)
  const setDraft = useSetDraft()
  const clearDraft = useClearDraft()

  const [isInEditMode, setIsInEditMode] = useState(!uuid)
  const [entity, setEntity] = useState(null)
  const [updatedEntity, setUpdatedEntity] = useState(() =>
    !uuid ? (draft ?? null) : null,
  )
  const [entitySections, setEntitySections] = useState(null)
  const [pageTitle, setPageTitle] = useState(null)
  const [loading, setLoading] = useState(!!uuid)
  const [errors, setErrors] = useState(null)
  const [alert, setAlert] = useState(null)
  const [selectedPanel, setSelectedPanel] = useState(null)
  const [historicSnapshot, setHistoricSnapshot] = useState(null)
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)
  const { setNewItemKey, scrollRefFor } = useScrollToNew()

  const liveEntity = isInEditMode ? updatedEntity : entity

  const { setBreadcrumbs } = usePageTitle()
  const { user } = useUser()
  const timezone = user?.settings?.timezone || 'UTC'
  const userDateFormat = user?.settings?.dateFormat || 'iso'
  const [showDraftAlert, setShowDraftAlert] = useState(!uuid && draft != null)

  const debouncedSetDraft = useMemo(
    () => debounce((value) => setDraft(endpoint, value), 400),
    [endpoint, setDraft],
  )

  useEffect(() => debouncedSetDraft.cancel, [debouncedSetDraft])

  const updateDraft = useCallback(
    (entityToSave) => {
      if (uuid || !entityToSave) return
      debouncedSetDraft(entityToSave)
    },
    [uuid, debouncedSetDraft],
  )

  const applyUpdate = useCallback(
    (producer) => {
      setUpdatedEntity((prev) => {
        const next = producer(prev)
        updateDraft(next)
        return next
      })
    },
    [updateDraft],
  )

  const updateEntity = useCallback(
    (key, value) => {
      applyUpdate((prev) => ({ ...prev, [key]: value }))
    },
    [applyUpdate],
  )

  const save = async () => {
    setAlert(null)
    const url = uuid ? `${endpoint}/${uuid}/` : endpoint
    const method = uuid ? 'PUT' : 'POST'
    try {
      const payload = preparePayload(
        { ...(updatedEntity ?? entity) },
        entitySections,
      )

      const { data, status } = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (status === 400) {
        setErrors(data.errors)
        return
      }
      if (status >= 200 && status < 300) {
        setEntity(data.data)
        setIsInEditMode(false)
        setErrors(null)
        setHistoryRefreshKey((k) => k + 1)
        setUpdatedEntity(null)
        clearDraft(endpoint)
        navigate(`/${resolveNavPath(endpoint)}/${uuid ?? data.data.uuid}`, {
          replace: true,
        })
      } else {
        setAlert(data?.message || `Could not ${uuid ? 'update' : 'create'}!`)
      }
    } catch (error) {
      console.error(
        `Error ${uuid ? 'updating' : 'creating'} ${endpoint}:`,
        error,
      )
    }
  }

  const addSection = (key, fields) => {
    const currentItems = (updatedEntity ?? entity)?.[key] ?? []
    setNewItemKey(`${key}-${currentItems.length}`)
    applyUpdate((prev) => ({
      ...prev,
      [key]: [...(prev?.[key] ?? []), emptyRecord(fields)],
    }))
  }

  useEffect(() => {
    if (!isInEditMode || !entitySections) return
    entitySections.forEach((section) => {
      if (section.repeater) {
        setUpdatedEntity((prev) => {
          const items = prev?.[section.key] ?? []
          if (items.length > 0) return prev
          return { ...prev, [section.key]: [emptyRecord(section.fields)] }
        })
      }
      section.fieldgroups?.forEach((group) => {
        if (!group.repeater) return
        setUpdatedEntity((prev) => {
          const items = prev?.[group.key] ?? []
          if (items.length > 0) return prev
          return { ...prev, [group.key]: [emptyRecord(group.fields)] }
        })
      })
    })
  }, [isInEditMode, entitySections])

  // Fetch meta if needed when navigating directly to /create
  useEffect(() => {
    if (uuid || entityMeta) return
    fetchWithAuth(`${endpoint}?pageSize=1&meta=true`)
      .then(({ data }) => setMeta(endpoint, data.meta))
      .catch(console.error)
  }, [uuid, entityMeta, endpoint, setMeta])

  useEffect(() => {
    if (uuid || !entityMeta) return

    // Entity creation
    setPageTitle('Add new')
    setEntitySections(entityMeta.meta.sections)
    setBreadcrumbs([resolveNavPath(endpoint), 'add new'])
    return () => setBreadcrumbs([])
  }, [uuid, entityMeta, endpoint, setBreadcrumbs])

  useEffect(() => {
    if (!uuid) return

    const fetchEntity = async () => {
      try {
        const hasMeta = !!entityMetaRef.current
        const { data } = await fetchWithAuth(
          `${endpoint}/${uuid}${hasMeta ? '' : '?meta=true'}`,
        )
        setEntity(data.data)
        if (!hasMeta) {
          setMeta(endpoint, data.meta)
          setEntitySections(data.meta.sections)
        } else {
          setEntitySections(entityMetaRef.current.meta.sections)
        }
        setPageTitle(data.title)
        setBreadcrumbs([resolveNavPath(endpoint), data.title])
      } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error)
      } finally {
        setLoading(false)
      }
    }

    fetchEntity()
    return () => setBreadcrumbs([])
  }, [uuid, endpoint, setMeta, setBreadcrumbs])

  const effectiveEditMode = isInEditMode && !historicSnapshot
  const formattedSnapshot = historicSnapshot
    ? formatDateTime(historicSnapshot.createdAt, timezone, userDateFormat)
    : null
  const pageLabel = pageTitle ?? entity?.name ?? entity?.uuid
  const title = formattedSnapshot
    ? `History of ${pageLabel} - ${formattedSnapshot}`
    : pageLabel

  const sections = [
    {
      content: (
        <Box sx={{ width: '100%' }}>
          {showDraftAlert && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    clearDraft(endpoint)
                    setUpdatedEntity(null)
                    setShowDraftAlert(false)
                  }}
                >
                  Discard all changes
                </Button>
              }
            >
              There are unsaved changes!
            </Alert>
          )}
          <Masonry
            columns={{ sm: 1, md: 2 }}
            spacing={2}
            // set minHeight to prevent jump on item height change
            sx={{ minHeight: '2000px', width: '100%', mx: 'auto' }}
          >
            {entitySections
              ?.filter(
                (s) =>
                  !s.hidden &&
                  (!s.condition ||
                    evaluateCondition(
                      s.condition,
                      historicSnapshot ?? liveEntity,
                    )),
              )
              .map((section) => {
                const sharedProps = {
                  ...section,
                  isInEditMode: effectiveEditMode,
                  errors: errors,
                  addSection: () => addSection(section.key, section.fields),
                  responsibleRoles,
                }

                if (section.repeater) {
                  const items =
                    (historicSnapshot ?? liveEntity)?.[section.key] ?? []
                  return items.map((item, index) => (
                    <DetailCard
                      {...sharedProps}
                      key={`${section.key}-${index}`}
                      ref={scrollRefFor(`${section.key}-${index}`)}
                      repeater={index === items.length - 1}
                      data={item}
                      update={(key, value) => {
                        applyUpdate((prev) => ({
                          ...prev,
                          [section.key]: updateAtIndex(
                            prev?.[section.key] ?? [],
                            index,
                            { [key]: value },
                          ),
                        }))
                      }}
                      removeSection={
                        items.length > 1 && index > 0
                          ? () => {
                              applyUpdate((prev) => ({
                                ...prev,
                                [section.key]: (
                                  prev?.[section.key] ?? []
                                ).filter((_, i) => i !== index),
                              }))
                            }
                          : undefined
                      }
                    />
                  ))
                }

                return (
                  <DetailCard
                    {...sharedProps}
                    key={section.key}
                    repeater={false}
                    data={historicSnapshot ?? liveEntity}
                    update={updateEntity}
                  />
                )
              })}
          </Masonry>
        </Box>
      ),
    },
  ]

  const panelMeta = {
    history: {
      title: 'History',
      overlay: false,
      content: (
        <HistoryTimeLine
          endpoint={endpoint}
          uuid={uuid}
          isActive={selectedPanel === 'history'}
          onSnapshotSelect={setHistoricSnapshot}
          selectedSnapshotId={historicSnapshot?.createdAt ?? null}
          timezone={timezone}
          userDateFormat={userDateFormat}
          refreshKey={historyRefreshKey}
          disabled={isInEditMode}
        />
      ),
    },
  }

  const actions = (
    <Stack direction="row" spacing={2}>
      {isInEditMode ? (
        <>
          <Button
            variant="outlined"
            onClick={() => {
              if (!uuid) {
                navigate(`/${resolveNavPath(endpoint)}`)
              } else {
                setUpdatedEntity(null)
                setErrors(null)
                setIsInEditMode(false)
              }
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={save}>
            {!uuid ? 'Create' : 'Save'}
          </Button>
        </>
      ) : (
        <>
          {entityMeta?.meta.options.history && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setSelectedPanel('history')}
              startIcon={<HistoryRounded />}
            >
              View history
            </Button>
          )}
          {!historicSnapshot && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setUpdatedEntity({ ...entity })
                setIsInEditMode(true)
              }}
            >
              Edit
            </Button>
          )}
        </>
      )}
    </Stack>
  )

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, minWidth: 400 }}>
          {historicSnapshot && (
            <Alert
              severity="info"
              sx={{ mx: 2, mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setHistoricSnapshot(null)}
                >
                  View current
                </Button>
              }
            >
              Viewing state from {formattedSnapshot}
            </Alert>
          )}
          <DetailPageTemplate
            title={title}
            isLoading={loading}
            actions={actions}
            sections={sections}
          />
        </Box>
        <SidePanel
          open={selectedPanel !== null}
          title={panelMeta[selectedPanel]?.title}
          overlay={panelMeta[selectedPanel]?.overlay}
          onClose={() => {
            setSelectedPanel(null)
          }}
        >
          {panelMeta[selectedPanel]?.content}
        </SidePanel>
      </Box>
      <Snackbar
        open={alert}
        onClose={() => {
          setAlert(null)
        }}
      >
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => {
            setAlert(null)
          }}
        >
          {alert}
        </Alert>
      </Snackbar>
    </>
  )
}

export default EntityDetailTemplate
